import { FEMALE_KEYWORDS, UPLOAD_URL } from "../utils/constants";
//@ts-ignore
import malePlaceholder from "../assets/images/male.png";
//@ts-ignore
import femalePlaceholder from "../assets/images/female.png";
import { MatchListEntry } from "../types";
import { postRequest } from "./api.helper";
import { toast } from "react-toastify";
import { toSlug } from "./function.helper";
import { Share } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";

const normalizeGender = (value?: string): string | undefined => {
  if (!value || typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  return normalized || undefined;
};

const getGenderPlaceholder = (gender?: string): string => {
  const normalized = normalizeGender(gender);
  if (normalized && FEMALE_KEYWORDS.has(normalized)) {
    return femalePlaceholder;
  }
  return malePlaceholder;
};

const normalizeImage = (image: string | undefined, fallback: string) => {
  if (!image) return fallback;
  const trimmed = image.trim();
  if (!trimmed) return fallback;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const sanitized = trimmed.replace(/^\/+/, "");
  if (!sanitized) return fallback;
  const base = UPLOAD_URL.replace(/\/+$/, "");
  if (sanitized.startsWith("uploads/")) {
    return `${base}/${sanitized.replace(/^uploads\//, "")}`;
  }
  return `${base}/users/profile_pic/${sanitized}`;
};

const copyProfileId = async (profileId?: string) => {
  if (!profileId) return;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(profileId);
    } else {
      const input = document.createElement("textarea");
      input.value = profileId;
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    toast.success("Profile ID copied");
  } catch (error) {
    console.error("Failed to copy profile ID:", error);
    toast.error("Unable to copy profile ID");
  }
};

const handleViewProfile = async (match: MatchListEntry, navigate: any) => {
  const targetId = match.maritalId;

  if (!targetId) {
    console.warn("Unable to determine profile id for match", match);
    return;
  }

  // ✅ Save current list scroll position
  try {
    const path = window.location.pathname + window.location.search;
    sessionStorage.setItem(`matches:scroll:${path}`, String(window.scrollY));
    // Legacy keys (safe fallback for older restores)
    sessionStorage.setItem("matches:lastScrollY", String(window.scrollY));
    sessionStorage.setItem("matches:lastPath", path);
  } catch {}

  const to = `/admin/profile/detail/${encodeURIComponent(match.maritalId)}`;

  try {
    navigate(to, { state: { match } });
  } catch (error) {
    console.error("Error recording profile view:", error);
    navigate(to, { state: { match } });
  }
};

const handleShortList = async (
  match: MatchListEntry,
  profiles: MatchListEntry[],
  setProfiles: (profiles: MatchListEntry[]) => void
) => {
  try {
    const targetId = match.id;
    if (!targetId) {
      console.warn("Unable to determine profile id for match", match);
      return;
    }
    // 👇 Call backend API
    const response = await postRequest("/bureau/profiles/shortlist/toggle", {
      profileId: targetId,
    });
    if (response?.success) {
      toast.success(response.message || "Shortlist updated successfully");

      // ✅ Update local profiles to mark this one as not interested
      const updatedProfiles = profiles.map((pf) =>
        pf.id === targetId ? { ...pf, isShortlisted: !match.isShortlisted } : pf
      );

      setProfiles(updatedProfiles);
    } else {
      toast.warn(response?.message || "Could not update shortlist");
    }
  } catch (error) {
    console.error("Error toggling shortlist:", error);
    toast.error("Something went wrong while updating shortlist");
  }
};

const handleSwitchProfile = (match: MatchListEntry) => {
  const martialId = match.maritalId;
  const password = match.password;

  const url = `https://meemarriage.com/login?martialId=${encodeURIComponent(
    martialId
  )}&password=${encodeURIComponent(password)}`;

  window.open(url, "_blank"); // open in new tab
};

// const handleShareProfile = async (match: MatchListEntry) => {
//   try {
//     const targetId = match.maritalId;
//     if (!targetId) {
//       toast.warn("Profile ID missing");
//       return;
//     }

//     const bureauSlug = (match?.bureauName ?? "").replace(/\s+/g, "");
//     const shareUrl = `https://matrimonystudio.in/${match.bureauId}/${bureauSlug}/${targetId}`;

//     const shareData = {
//       title: "MeeMarriage Profile",
//       text: "💍 Check out this profile on MeeMarriage",
//       url: shareUrl,
//     };

//     // ✅ Use native share ONLY if supported
//     if (navigator.share) {
//       await navigator.share(shareData);
//       return;
//     }

//     // ✅ Fallback → copy to clipboard
//     await navigator.clipboard.writeText(shareUrl);
//     toast.info("Profile link copied. You can share it anywhere.");
//   } catch (error) {
//     console.error("Share failed:", error);
//     // toast.error("Unable to share profile");
//   }
// };

const handleShareProfile = async (match: MatchListEntry) => {
  try {
    const targetId = match.maritalId;
    if (!targetId) {
      toast.warn("Profile ID missing");
      return;
    }

    const bureauSlug = (match?.bureauName ?? "").replace(/\s+/g, "");
    const shareUrl = `https://matrimonystudio.in/${match.bureauId}/${bureauSlug}/${targetId}`;

    // ✅ Capacitor native share
    if (Capacitor.isNativePlatform()) {
      await Share.share({
        title: "MeeMarriage Profile",
        text: "💍 Check out this profile on MeeMarriage",
        url: shareUrl,
        dialogTitle: "Share Profile",
      });
      return;
    }

    // ✅ Web share (mobile browsers)
    if (navigator.share) {
      await navigator.share({
        title: "MeeMarriage Profile",
        text: "💍 Check out this profile on MeeMarriage",
        url: shareUrl,
      });
      return;
    }

    // ✅ fallback copy
    await navigator.clipboard.writeText(shareUrl);
    toast.info("Profile link copied. You can share it anywhere.");
  } catch (error) {
    console.error("Share failed:", error);
    toast.error("Unable to share profile");
  }
};

const handleReportProfile = (match: MatchListEntry, navigate: any) => {
  if (!match?.id) {
    console.warn("No profile ID found for report");
    return;
  }

  // Navigate to the report profile page and pass profile info
  navigate("/profile/report", {
    state: {
      profileId: match.id,
      profileName: match.fullName || "Unknown User",
      profileMartialId: match.id,
    },
  });
};

const handleManageProfile = (
  match: MatchListEntry,
  navigate: any,
  options?: { returnTo?: string },
) => {
  navigate(`/admin/profile/manage/${match.maritalId}`, {
    state: options?.returnTo ? { returnTo: options.returnTo } : undefined,
  });
};

export {
  normalizeGender,
  getGenderPlaceholder,
  normalizeImage,
  copyProfileId,
  handleShareProfile,
  handleViewProfile,
  handleShortList,
  handleSwitchProfile,
  handleReportProfile,
  handleManageProfile,
};
